import { Button, Card, Checkbox, Col, Drawer, InputNumber, Row, Space, Switch } from 'antd'
import dayjs from 'dayjs'
import { User } from '@/pages/system/user/types'
import { WEEKDAY_OPTIONS } from '../constants'
import { GenerateTemplateItem } from '../page-types'
import { StudentClassCurrentConfig } from '../types'
import { ConfigTemplateEditor } from './ConfigTemplateEditor'

type ClassConfigDrawerProps = {
  open: boolean
  width: number | string
  currentConfig: StudentClassCurrentConfig | null
  isConfigDirty: boolean
  savingCurrentConfig: boolean
  weekdays: number[]
  isAllDayPricing: boolean
  monthlyPrice: number | undefined
  templates: GenerateTemplateItem[]
  teachers: User[]
  teachersLoading: boolean
  onClose: () => void
  onSaveCurrentConfig: () => void
  onAllDayPricingChange: (checked: boolean) => void
  onMonthlyPriceChange: (value: number | undefined) => void
  onWeekdaysChange: (value: number[]) => void
  onTemplateChange: (localId: string, next: Partial<GenerateTemplateItem>) => void
  onAddTemplate: () => void
  onRemoveTemplate: (localId: string) => void
}

export function ClassConfigDrawer({
  open,
  width,
  currentConfig,
  isConfigDirty,
  savingCurrentConfig,
  weekdays,
  isAllDayPricing,
  monthlyPrice,
  templates,
  teachers,
  teachersLoading,
  onClose,
  onSaveCurrentConfig,
  onAllDayPricingChange,
  onMonthlyPriceChange,
  onWeekdaysChange,
  onTemplateChange,
  onAddTemplate,
  onRemoveTemplate,
}: ClassConfigDrawerProps) {
  return (
    <Drawer
      title="上课配置"
      open={open}
      onClose={onClose}
      width={width}
      destroyOnHidden
      extra={
        <Space>
          <Button loading={savingCurrentConfig} disabled={!isConfigDirty} onClick={onSaveCurrentConfig}>
            保存配置
          </Button>
        </Space>
      }
    >
      <div className="flex flex-col gap-4">
        <Card size="small" title="当前配置">
          <div className="mb-4 text-sm text-gray-600">
            {currentConfig?.createdAt
              ? `配置创建时间：${dayjs(currentConfig.createdAt).format('YYYY-MM-DD HH:mm')}`
              : '暂无当前配置'}
          </div>

          <Row gutter={[12, 12]} className="mb-4">
            <Col xs={24}>
              <div className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-3">
                <div>
                  <div className="text-sm text-gray-700">全天班计费</div>
                  <div className="mt-1 text-xs text-gray-500">
                    开启后，所有模板统一按全天班处理，模板价格全部留空
                  </div>
                </div>
                <Switch checked={isAllDayPricing} onChange={onAllDayPricingChange} />
              </div>
            </Col>
            <Col xs={24}>
              <div className="mb-2 text-sm text-gray-600">全天班费用</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                precision={2}
                value={monthlyPrice}
                disabled={!isAllDayPricing}
                placeholder={isAllDayPricing ? '启用全天班计费后必填' : '请先开启全天班计费'}
                onChange={(value) => onMonthlyPriceChange(value ?? undefined)}
              />
            </Col>
            <Col xs={24}>
              <div className="mb-2 text-sm text-gray-600">周几（多选）</div>
              <div className="mb-2 text-xs text-gray-500">
                可不选，表示不固定上课日；保存后可在批量生成时继续选择日期范围
              </div>
              <Space wrap className="mb-2">
                <Button size="small" onClick={() => onWeekdaysChange([1, 2, 3, 4, 5])}>
                  周一到周五
                </Button>
                <Button size="small" onClick={() => onWeekdaysChange([6, 7])}>
                  周末
                </Button>
                <Button size="small" onClick={() => onWeekdaysChange([])}>
                  清空
                </Button>
              </Space>
              <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                <Checkbox.Group
                  className="flex flex-wrap gap-x-3 gap-y-2"
                  options={WEEKDAY_OPTIONS}
                  value={weekdays}
                  onChange={(values) => onWeekdaysChange(values as number[])}
                />
              </div>
            </Col>
          </Row>

          <ConfigTemplateEditor
            templates={templates}
            isAllDayPricing={isAllDayPricing}
            teachers={teachers}
            teachersLoading={teachersLoading}
            onTemplateChange={onTemplateChange}
            onAddTemplate={onAddTemplate}
            onRemoveTemplate={onRemoveTemplate}
          />
        </Card>
      </div>
    </Drawer>
  )
}
